import { lerpColor } from '../../color';
import { calmAt, calmCenter, catenary, glowDot, grain, rgba, starPath, vignette, type Painter } from '../../art/paint';
import { hsl, type World as Look } from '../../world';

const TAU = Math.PI * 2;
const FAR = hsl(10, 36, 16);

interface Tones {
  readonly bg: number;
  readonly far: number;
  readonly ceil: number;
  readonly ink: number;
  readonly rim: number;
  readonly L: number;
  readonly gold: number;
  readonly violet: number;
}

interface Banner {
  readonly x: number;
  readonly y: number;
  readonly s: number;
  readonly foot: number;
}

interface Layout {
  readonly portrait: boolean;
  readonly horizon: number;
  readonly roof: number;
  readonly palio: { readonly x: number; readonly y: number; readonly r: number; readonly foot: number };
  readonly banners: readonly Banner[];
  readonly church: { readonly x: number; readonly s: number };
  readonly torches: readonly number[];
  readonly drum: number;
  readonly drumFoot: number;
}

function tonesOf(look: Look): Tones {
  const p = look.palette;
  return { bg: p.bg, far: FAR, ceil: look.ceil, ink: p.ink, rim: p.rim, L: p.incoming.circle, gold: p.held, violet: p.spot };
}

function layoutOf(p: Painter): Layout {
  const u = p.unit;
  if (p.w / p.h < 0.8) {
    const horizon = p.h * 0.37;
    const drum = Math.min(p.w * 0.17, p.h * 0.085);
    const s = Math.min(u * 0.19, p.w * 0.12);
    const r = Math.min(u * 0.27, p.w * 0.16);
    return {
      portrait: true,
      horizon,
      roof: p.h * 0.215,
      palio: { x: p.w * 0.24, y: Math.max(p.h * 0.275, p.h * 0.2 + r), r, foot: horizon + u * 0.1 },
      banners: [
        { x: p.w * 0.89, y: Math.max(p.h * 0.24, p.h * 0.2 + s * 0.5), s, foot: horizon + u * 0.08 },
        { x: Math.min(p.w * 0.075, p.w * 0.24 - r - s * 0.5), y: p.h * 0.3, s: s * 0.75, foot: horizon + u * 0.04 },
      ],
      church: { x: p.w * 0.4, s: Math.min(u * 0.11, p.w * 0.055) },
      torches: [p.w * 0.16, p.w * 0.97],
      drum,
      drumFoot: p.h * 0.875,
    };
  }
  const horizon = p.h * 0.8;
  const px = Math.max(u * 0.3, Math.min(p.w * 0.15, p.cx - u * 1.18));
  const bx = Math.min(p.w * 0.87, p.cx + u * 1.3);
  return {
    portrait: false,
    horizon,
    roof: p.h * 0.22,
    palio: { x: px, y: p.h * 0.35, r: u * 0.21, foot: p.h * 0.95 },
    banners: [
      { x: bx, y: p.h * 0.3, s: u * 0.21, foot: horizon + u * 0.12 },
      { x: Math.min(p.w - u * 0.12, bx + u * 0.36), y: p.h * 0.4, s: u * 0.15, foot: horizon + u * 0.06 },
    ],
    church: { x: p.cx + u * 1.1, s: u * 0.11 },
    torches: [u * 0.12, p.w - u * 0.12],
    drum: u * 0.3,
    drumFoot: p.h - 3,
  };
}

function sky(p: Painter, t: Tones, lay: Layout): void {
  const ctx = p.ctx;
  const g = ctx.createLinearGradient(0, 0, 0, lay.horizon);
  g.addColorStop(0, rgba(lerpColor(t.bg, t.ink, 0.55)));
  g.addColorStop(0.6, rgba(t.bg));
  g.addColorStop(1, rgba(lerpColor(t.bg, t.far, 0.85)));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, p.w, lay.horizon + 1);
  const street = ctx.createLinearGradient(0, lay.horizon, 0, p.h);
  street.addColorStop(0, rgba(lerpColor(t.far, t.ceil, 0.2)));
  street.addColorStop(1, rgba(lerpColor(t.bg, t.ink, 0.3)));
  ctx.fillStyle = street;
  ctx.fillRect(0, lay.horizon, p.w, p.h - lay.horizon);
  const haze = ctx.createLinearGradient(0, lay.horizon - p.unit * 0.6, 0, lay.horizon);
  haze.addColorStop(0, rgba(t.ceil, 0));
  haze.addColorStop(1, rgba(t.ceil, 0.32));
  ctx.fillStyle = haze;
  ctx.fillRect(0, lay.horizon - p.unit * 0.6, p.w, p.unit * 0.6);
  for (let i = 0; i < 34; i++) {
    const x = p.rng.float() * p.w;
    const y = p.rng.float() * lay.horizon * 0.7;
    if (calmAt(p, x, y, 0.95)) continue;
    ctx.fillStyle = rgba(t.rim, p.rng.range(0.15, 0.42));
    ctx.beginPath();
    ctx.arc(x, y, p.rng.range(0.6, 1.3), 0, TAU);
    ctx.fill();
  }
}

function archWindow(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
  ctx.beginPath();
  ctx.moveTo(x - w / 2, y + h);
  ctx.lineTo(x - w / 2, y + w / 2);
  ctx.arc(x, y + w / 2, w / 2, Math.PI, 0);
  ctx.lineTo(x + w / 2, y + h);
  ctx.closePath();
}

function houses(p: Painter, t: Tones, lay: Layout): void {
  const ctx = p.ctx;
  const u = p.unit;
  const tints = [t.L, t.gold, t.violet, t.far];
  const room = lay.horizon - lay.roof;
  let x = -p.rng.range(4, 24);
  while (x < p.w + 4) {
    const w = u * p.rng.range(0.2, 0.3);
    const floors = p.rng.int(2, 4);
    const fh = Math.min(u * 0.13, (room * 0.8) / (floors + 0.6));
    const h = fh * floors + fh * 0.3;
    const top = lay.horizon - h;
    const tone = lerpColor(lerpColor(t.far, t.ceil, p.rng.range(0.3, 0.65)), p.rng.pick(tints), p.rng.range(0.05, 0.13));
    ctx.fillStyle = rgba(tone);
    ctx.fillRect(x, top, w, h + 2);
    ctx.fillStyle = rgba(lerpColor(tone, t.ink, 0.4));
    ctx.fillRect(x - 1, top - 3, w + 2, 4);
    ctx.fillRect(x - 1, top + fh * 1.3 - 2, w + 2, 2.5);
    if (p.rng.chance(0.55)) {
      ctx.fillStyle = rgba(tone);
      ctx.beginPath();
      ctx.moveTo(x + w * 0.08, top - 3);
      ctx.quadraticCurveTo(x + w * 0.2, top - fh * 0.4, x + w * 0.5, top - fh * 0.42);
      ctx.quadraticCurveTo(x + w * 0.8, top - fh * 0.4, x + w * 0.92, top - 3);
      ctx.closePath();
      ctx.fill();
    } else {
      ctx.fillStyle = rgba(lerpColor(t.far, t.ink, 0.35));
      ctx.beginPath();
      ctx.moveTo(x - 2, top - 3);
      ctx.lineTo(x + w * 0.5, top - fh * 0.32);
      ctx.lineTo(x + w + 2, top - 3);
      ctx.closePath();
      ctx.fill();
    }
    const cols = w > u * 0.25 ? 3 : 2;
    const ww = w / (cols * 2.1);
    for (let f = 0; f < floors; f++) {
      const y = top + fh * 0.3 + fh * f + fh * 0.2;
      for (let c = 0; c < cols; c++) {
        const wx = x + (w * (c + 0.5)) / cols;
        const door = f === floors - 1 && c === Math.floor(cols / 2);
        const wh = door ? fh * 0.78 : fh * 0.55;
        const lit = !door && p.rng.chance(0.22);
        archWindow(ctx, wx, door ? lay.horizon - wh : y, door ? ww * 1.25 : ww, wh);
        ctx.fillStyle = lit ? rgba(lerpColor(t.far, t.gold, 0.45), 0.55) : rgba(lerpColor(tone, t.ink, 0.55));
        ctx.fill();
      }
      if (f === 1 && p.rng.chance(0.5)) {
        ctx.strokeStyle = rgba(lerpColor(tone, t.rim, 0.14));
        ctx.lineWidth = 1;
        ctx.strokeRect(x + w * 0.1, y + fh * 0.42, w * 0.8, fh * 0.14);
      }
    }
    x += w;
  }
}

function church(p: Painter, t: Tones, lay: Layout): void {
  const ctx = p.ctx;
  const { x, s } = lay.church;
  const base = lay.horizon;
  const body = Math.min(s * 3.4, (base - lay.roof) * 0.62);
  const tower = Math.min(body + s * 1.4, (base - lay.roof) * 0.86);
  const tone = lerpColor(t.far, t.ceil, 0.5);
  ctx.fillStyle = rgba(tone);
  ctx.beginPath();
  ctx.moveTo(x - s * 1.4, base);
  ctx.lineTo(x - s * 1.4, base - body);
  ctx.bezierCurveTo(x - s * 1.1, base - body - s * 0.2, x - s * 0.6, base - body - s * 1.1, x, base - body - s * 1.1);
  ctx.bezierCurveTo(x + s * 0.6, base - body - s * 1.1, x + s * 1.1, base - body - s * 0.2, x + s * 1.4, base - body);
  ctx.lineTo(x + s * 1.4, base);
  ctx.closePath();
  ctx.fill();
  for (const side of [-1, 1]) {
    const tx = x + side * s * 1.9;
    ctx.fillStyle = rgba(tone);
    ctx.fillRect(tx - s * 0.5, base - tower, s, tower);
    ctx.beginPath();
    ctx.moveTo(tx - s * 0.5, base - tower);
    ctx.quadraticCurveTo(tx - s * 0.55, base - tower - s * 0.7, tx, base - tower - s * 0.85);
    ctx.quadraticCurveTo(tx + s * 0.55, base - tower - s * 0.7, tx + s * 0.5, base - tower);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = rgba(tone);
    ctx.lineWidth = Math.max(1, s * 0.08);
    ctx.beginPath();
    ctx.moveTo(tx, base - tower - s * 0.85);
    ctx.lineTo(tx, base - tower - s * 1.25);
    ctx.moveTo(tx - s * 0.14, base - tower - s * 1.1);
    ctx.lineTo(tx + s * 0.14, base - tower - s * 1.1);
    ctx.stroke();
    archWindow(ctx, tx, base - tower + s * 0.35, s * 0.36, s * 0.6);
    ctx.fillStyle = rgba(lerpColor(tone, t.ink, 0.55));
    ctx.fill();
  }
  ctx.fillStyle = rgba(lerpColor(t.far, t.gold, 0.42), 0.5);
  ctx.beginPath();
  ctx.arc(x, base - body - s * 0.35, s * 0.28, 0, TAU);
  ctx.fill();
  archWindow(ctx, x, base - s * 1.3, s * 0.7, s * 1.3);
  ctx.fillStyle = rgba(lerpColor(tone, t.ink, 0.6));
  ctx.fill();
}

function cobbles(p: Painter, t: Tones, lay: Layout): void {
  const ctx = p.ctx;
  const tone = lerpColor(t.far, t.ceil, 0.55);
  for (let i = 0; i < 90; i++) {
    const x = p.rng.float() * p.w;
    const y = lay.horizon + 4 + p.rng.float() * (p.h - lay.horizon - 4);
    if (calmAt(p, x, y, 1)) continue;
    const r = p.unit * p.rng.range(0.012, 0.022) * (0.6 + (0.4 * (y - lay.horizon)) / Math.max(1, p.h - lay.horizon));
    ctx.fillStyle = rgba(tone, p.rng.range(0.1, 0.22));
    ctx.beginPath();
    ctx.ellipse(x, y, r * 1.5, r, p.rng.range(-0.2, 0.2), 0, TAU);
    ctx.fill();
  }
}

function torch(p: Painter, t: Tones, x: number, foot: number, s: number): void {
  const ctx = p.ctx;
  const top = foot - s * 4.2;
  glowDot(ctx, x, top - s * 0.5, s * 5.5, t.L, 0.09);
  glowDot(ctx, x, top - s * 0.4, s * 2.8, t.gold, 0.16);
  ctx.strokeStyle = rgba(lerpColor(t.far, t.ink, 0.3));
  ctx.lineWidth = Math.max(1.5, s * 0.18);
  ctx.beginPath();
  ctx.moveTo(x, foot);
  ctx.lineTo(x, top);
  ctx.stroke();
  ctx.fillStyle = rgba(lerpColor(t.far, t.ceil, 0.7));
  ctx.beginPath();
  ctx.moveTo(x - s * 0.42, top - s * 0.3);
  ctx.lineTo(x + s * 0.42, top - s * 0.3);
  ctx.lineTo(x + s * 0.2, top + s * 0.25);
  ctx.lineTo(x - s * 0.2, top + s * 0.25);
  ctx.closePath();
  ctx.fill();
  const flame = (scale: number, color: number, alpha: number): void => {
    ctx.fillStyle = rgba(color, alpha);
    ctx.beginPath();
    ctx.moveTo(x + s * 0.06 * scale, top - s * 1.5 * scale - s * 0.3);
    ctx.quadraticCurveTo(x + s * 0.5 * scale, top - s * 0.6 * scale - s * 0.3, x, top - s * 0.3);
    ctx.quadraticCurveTo(x - s * 0.5 * scale, top - s * 0.6 * scale - s * 0.3, x + s * 0.06 * scale, top - s * 1.5 * scale - s * 0.3);
    ctx.fill();
  };
  flame(1, lerpColor(t.gold, t.L, 0.4), 0.55);
  flame(0.55, lerpColor(t.gold, t.rim, 0.5), 0.5);
}

function garland(p: Painter, t: Tones, x0: number, y0: number, x1: number, y1: number, sagBy: number): void {
  const ctx = p.ctx;
  ctx.strokeStyle = rgba(lerpColor(t.far, t.rim, 0.25), 0.6);
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let i = 0; i <= 24; i++) {
    const at = catenary(x0, y0, x1, y1, sagBy, i / 24);
    if (i === 0) ctx.moveTo(at.x, at.y);
    else ctx.lineTo(at.x, at.y);
  }
  ctx.stroke();
  const span = Math.hypot(x1 - x0, y1 - y0);
  const count = Math.max(4, Math.floor(span / Math.max(10, p.unit * 0.05)));
  const tones = [t.gold, t.violet, t.L];
  for (let i = 1; i < count; i++) {
    const at = catenary(x0, y0, x1, y1, sagBy, i / count);
    if (calmAt(p, at.x, at.y, 0.95)) continue;
    ctx.fillStyle = rgba(tones[i % tones.length] ?? t.gold, 0.5);
    ctx.beginPath();
    ctx.arc(at.x, at.y + 3, Math.max(1.5, p.unit * 0.009), 0, TAU);
    ctx.fill();
  }
}

function banner(p: Painter, t: Tones, b: Banner): void {
  const ctx = p.ctx;
  const { x, y, s } = b;
  const h = s * 1.35;
  const gold = lerpColor(t.far, t.gold, 0.45);
  ctx.strokeStyle = rgba(lerpColor(t.far, t.ceil, 0.45));
  ctx.lineWidth = Math.max(1.5, s * 0.05);
  ctx.beginPath();
  ctx.moveTo(x, b.foot);
  ctx.lineTo(x, y - s * 0.22);
  ctx.stroke();
  ctx.fillStyle = rgba(gold, 0.85);
  ctx.beginPath();
  ctx.moveTo(x, y - s * 0.42);
  ctx.lineTo(x + s * 0.07, y - s * 0.26);
  ctx.lineTo(x, y - s * 0.16);
  ctx.lineTo(x - s * 0.07, y - s * 0.26);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = rgba(gold, 0.85);
  ctx.lineWidth = Math.max(1.5, s * 0.04);
  ctx.beginPath();
  ctx.moveTo(x - s * 0.62, y);
  ctx.lineTo(x + s * 0.62, y);
  ctx.stroke();
  for (const side of [-1, 1]) {
    ctx.fillStyle = rgba(gold, 0.85);
    ctx.beginPath();
    ctx.arc(x + side * s * 0.64, y, Math.max(1.5, s * 0.05), 0, TAU);
    ctx.fill();
    ctx.strokeStyle = rgba(gold, 0.5);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x + side * s * 0.6, y);
    ctx.lineTo(x + side * s * 0.6, y + h * 0.45);
    ctx.stroke();
  }
  ctx.beginPath();
  ctx.moveTo(x - s / 2, y + 1);
  ctx.lineTo(x + s / 2, y + 1);
  ctx.lineTo(x + s / 2, y + h * 0.8);
  ctx.lineTo(x, y + h);
  ctx.lineTo(x - s / 2, y + h * 0.8);
  ctx.closePath();
  ctx.fillStyle = rgba(lerpColor(t.far, t.violet, 0.22));
  ctx.fill();
  ctx.strokeStyle = rgba(gold, 0.8);
  ctx.lineWidth = Math.max(1.2, s * 0.035);
  ctx.stroke();
  ctx.strokeStyle = rgba(gold, 0.45);
  ctx.lineWidth = 1;
  ctx.beginPath();
  const fringe = Math.max(4, Math.floor(s / 4));
  for (let i = 0; i <= fringe; i++) {
    const k = i / fringe;
    for (const side of [-1, 1]) {
      const fx = x + side * (s / 2) * (1 - k);
      const fy = y + h * 0.8 + h * 0.2 * k;
      ctx.moveTo(fx, fy);
      ctx.lineTo(fx, fy + s * 0.1);
    }
  }
  ctx.stroke();
  const my = y + h * 0.42;
  ctx.fillStyle = rgba(lerpColor(t.far, t.gold, 0.36));
  ctx.beginPath();
  ctx.arc(x, my, s * 0.24, 0, TAU);
  ctx.fill();
  starPath(ctx, x, my, s * 0.15, 0.45, 5, 0);
  ctx.fillStyle = rgba(lerpColor(t.far, t.L, 0.5), 0.8);
  ctx.fill();
}

function palio(p: Painter, t: Tones, lay: Layout): void {
  const ctx = p.ctx;
  const { x, y, r, foot } = lay.palio;
  const top = y - r * 0.66;
  const gold = lerpColor(t.far, t.gold, 0.45);
  glowDot(ctx, x, y, r * 2.6, t.gold, 0.08);
  ctx.strokeStyle = rgba(lerpColor(t.far, t.ceil, 0.5));
  ctx.lineWidth = Math.max(2, r * 0.06);
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x, foot);
  ctx.stroke();
  const dome = (): void => {
    ctx.beginPath();
    ctx.moveTo(x - r, y);
    ctx.bezierCurveTo(x - r * 0.96, y - r * 0.5, x - r * 0.5, top, x, top);
    ctx.bezierCurveTo(x + r * 0.5, top, x + r * 0.96, y - r * 0.5, x + r, y);
    ctx.closePath();
  };
  const fill = ctx.createLinearGradient(0, top, 0, y);
  fill.addColorStop(0, rgba(t.ceil));
  fill.addColorStop(1, rgba(lerpColor(t.far, t.violet, 0.18)));
  dome();
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = rgba(gold, 0.7);
  ctx.lineWidth = Math.max(1, r * 0.018);
  ctx.beginPath();
  for (let k = 1; k < 6; k++) {
    const xr = x - r + (2 * r * k) / 6;
    ctx.moveTo(x, top);
    ctx.quadraticCurveTo(x + (xr - x) * 0.85, y - r * 0.42, xr, y);
  }
  ctx.stroke();
  dome();
  ctx.strokeStyle = rgba(t.ink, 0.55);
  ctx.lineWidth = Math.max(1.2, r * 0.02);
  ctx.stroke();
  const n = 8;
  const seg = (2 * r) / n;
  const drop = r * 0.15;
  ctx.fillStyle = rgba(lerpColor(t.far, t.gold, 0.3));
  ctx.beginPath();
  ctx.moveTo(x - r, y);
  for (let i = 0; i < n; i++) {
    const sx = x - r + seg * i;
    ctx.quadraticCurveTo(sx + seg / 2, y + drop * 2, sx + seg, y);
  }
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = rgba(gold, 0.45);
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let fx = x - r + 2; fx < x + r - 1; fx += Math.max(3, r * 0.055)) {
    const k = ((fx - (x - r)) % seg) / seg;
    const fy = y + drop * 4 * k * (1 - k);
    ctx.moveTo(fx, fy);
    ctx.lineTo(fx, fy + r * 0.13);
  }
  ctx.stroke();
  ctx.fillStyle = rgba(t.gold, 0.55);
  for (let i = 0; i < n; i++) {
    ctx.beginPath();
    ctx.arc(x - r + seg * (i + 0.5), y + drop * 0.55, Math.max(1.2, r * 0.03), 0, TAU);
    ctx.fill();
  }
  ctx.fillStyle = rgba(lerpColor(t.far, t.gold, 0.5));
  ctx.beginPath();
  ctx.arc(x, top - r * 0.06, r * 0.07, 0, TAU);
  ctx.fill();
  if (p.level < 4) {
    ctx.strokeStyle = rgba(lerpColor(t.far, t.gold, 0.5));
    ctx.lineWidth = Math.max(1, r * 0.03);
    ctx.beginPath();
    ctx.moveTo(x, top - r * 0.12);
    ctx.lineTo(x, top - r * 0.26);
    ctx.stroke();
    return;
  }
  const cw = r * 0.17;
  const ch = r * 0.16;
  const cy = top - r * 0.12;
  ctx.fillStyle = rgba(lerpColor(t.far, t.gold, 0.55), 0.95);
  ctx.beginPath();
  ctx.moveTo(x - cw, cy);
  ctx.lineTo(x - cw * 1.1, cy - ch);
  ctx.lineTo(x - cw * 0.5, cy - ch * 0.55);
  ctx.lineTo(x, cy - ch * 1.25);
  ctx.lineTo(x + cw * 0.5, cy - ch * 0.55);
  ctx.lineTo(x + cw * 1.1, cy - ch);
  ctx.lineTo(x + cw, cy);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = rgba(t.violet, 0.55);
  for (const [dx, dy] of [
    [-cw * 1.1, -ch],
    [0, -ch * 1.25],
    [cw * 1.1, -ch],
  ] as const) {
    ctx.beginPath();
    ctx.arc(x + dx, cy + dy, Math.max(1.2, r * 0.03), 0, TAU);
    ctx.fill();
  }
}

function caboclo(p: Painter, t: Tones, x: number, base: number, r: number, spear: boolean): void {
  const ctx = p.ctx;
  const tones = [t.L, t.gold, t.violet, t.rim];
  if (spear) {
    const sx = x + r * 0.7;
    const tipX = x + r * 1.15;
    const tipY = base - r * 2.3;
    ctx.strokeStyle = rgba(lerpColor(t.far, t.ceil, 0.5), 0.8);
    ctx.lineWidth = Math.max(1, r * 0.05);
    ctx.beginPath();
    ctx.moveTo(sx, base);
    ctx.lineTo(tipX, tipY);
    ctx.stroke();
    ctx.lineWidth = Math.max(1, r * 0.04);
    for (let i = 0; i < 6; i++) {
      const a = -Math.PI / 2 + (i - 2.5) * 0.32;
      ctx.strokeStyle = rgba(lerpColor(t.far, tones[i % tones.length] ?? t.L, 0.4), 0.5);
      ctx.beginPath();
      ctx.moveTo(tipX, tipY + r * 0.15);
      ctx.quadraticCurveTo(tipX + Math.cos(a) * r * 0.3, tipY + Math.sin(a) * r * 0.2, tipX + Math.cos(a + 0.6) * r * 0.45, tipY + r * 0.35);
      ctx.stroke();
    }
  }
  const strips = 26;
  ctx.lineWidth = Math.max(1.2, r * 0.07);
  ctx.lineCap = 'round';
  for (let i = 0; i < strips; i++) {
    const a = Math.PI * 0.9 + (i / (strips - 1)) * Math.PI * 1.2;
    const len = r * p.rng.range(0.85, 1.05);
    ctx.strokeStyle = rgba(lerpColor(t.far, tones[(i + Math.floor(x)) % tones.length] ?? t.L, 0.36), 0.55);
    ctx.beginPath();
    ctx.moveTo(x + Math.cos(a) * r * 0.2, base + Math.sin(a) * r * 0.2);
    ctx.quadraticCurveTo(
      x + Math.cos(a) * len * 0.6,
      base + Math.sin(a) * len * 0.6 - r * 0.08,
      x + Math.cos(a) * len,
      base + Math.sin(a) * len * 0.85 + r * 0.12,
    );
    ctx.stroke();
  }
  ctx.lineCap = 'butt';
  ctx.fillStyle = rgba(lerpColor(t.far, t.ink, 0.4));
  ctx.beginPath();
  ctx.ellipse(x, base + r * 0.05, r * 0.24, r * 0.3, 0, 0, TAU);
  ctx.fill();
}

function crowd(p: Painter, t: Tones, lay: Layout): void {
  const r0 = lay.portrait ? Math.min(p.unit * 0.1, p.w * 0.075) : p.unit * 0.1;
  const base = lay.portrait ? lay.drumFoot - r0 * 0.1 : p.h - r0 * 0.15;
  const edge = lay.drum * 1.35;
  let i = 0;
  for (let x = edge + r0; x < p.w - edge - r0 * 0.5; x += r0 * p.rng.range(1.7, 2.1)) {
    const r = r0 * p.rng.range(0.85, 1.12);
    caboclo(p, t, x, base, r, i % 2 === 1);
    i += 1;
  }
}

function alfaia(p: Painter, t: Tones, x: number, bottom: number, s: number, sticks: boolean): void {
  const ctx = p.ctx;
  const h = s * 0.82;
  const top = bottom - h;
  const ry = s * 0.17;
  const wood = lerpColor(t.far, t.ceil, 0.42);
  ctx.fillStyle = rgba(wood);
  ctx.beginPath();
  ctx.moveTo(x - s / 2, top);
  ctx.lineTo(x - s / 2, bottom - ry * 0.4);
  ctx.ellipse(x, bottom - ry * 0.4, s / 2, ry * 0.6, 0, Math.PI, 0, true);
  ctx.lineTo(x + s / 2, top);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = rgba(lerpColor(wood, t.ink, 0.35));
  ctx.fillRect(x + s * 0.24, top, s * 0.26, h - ry * 0.4);
  const hoop = lerpColor(t.far, t.L, 0.18);
  ctx.fillStyle = rgba(hoop);
  ctx.fillRect(x - s / 2 - 1, top, s + 2, s * 0.09);
  ctx.fillRect(x - s / 2 - 1, bottom - s * 0.13, s + 2, s * 0.09);
  ctx.strokeStyle = rgba(lerpColor(t.far, t.gold, 0.42), 0.65);
  ctx.lineWidth = Math.max(1.2, s * 0.025);
  ctx.beginPath();
  const vs = 5;
  for (let i = 0; i <= vs * 2; i++) {
    const px = x - s / 2 + (s * i) / (vs * 2);
    const py = i % 2 === 0 ? top + s * 0.09 : bottom - s * 0.13;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.stroke();
  ctx.fillStyle = rgba(lerpColor(t.far, t.L, 0.3));
  ctx.beginPath();
  ctx.ellipse(x, top, s / 2, ry, 0, 0, TAU);
  ctx.fill();
  ctx.strokeStyle = rgba(t.ink, 0.5);
  ctx.lineWidth = 1.2;
  ctx.stroke();
  if (!sticks) return;
  ctx.lineCap = 'round';
  ctx.strokeStyle = rgba(lerpColor(t.far, t.ceil, 0.75));
  ctx.lineWidth = Math.max(2, s * 0.05);
  ctx.beginPath();
  ctx.moveTo(x - s * 0.32, top - ry * 0.2);
  ctx.lineTo(x + s * 0.22, top - s * 0.42);
  ctx.moveTo(x + s * 0.3, top - ry * 0.1);
  ctx.lineTo(x - s * 0.18, top - s * 0.44);
  ctx.stroke();
  ctx.lineCap = 'butt';
  ctx.fillStyle = rgba(lerpColor(t.far, t.ceil, 0.75));
  for (const [bx, by] of [
    [x + s * 0.22, top - s * 0.42],
    [x - s * 0.18, top - s * 0.44],
  ] as const) {
    ctx.beginPath();
    ctx.arc(bx, by, s * 0.05, 0, TAU);
    ctx.fill();
  }
}

export function paint(p: Painter, look: Look): void {
  const t = tonesOf(look);
  const lay = layoutOf(p);
  sky(p, t, lay);
  if (p.level >= 2) church(p, t, lay);
  houses(p, t, lay);
  cobbles(p, t, lay);
  const torchSize = p.unit * (lay.portrait ? 0.045 : 0.055);
  for (const x of lay.torches) torch(p, t, x, lay.horizon + p.unit * 0.04, torchSize);
  if (p.level >= 4) {
    if (lay.portrait) garland(p, t, -10, p.h * 0.04, p.w + 10, p.h * 0.04, p.h * 0.04);
    else {
      garland(p, t, -10, p.h * 0.14, p.w * 0.32, -6, p.h * 0.07);
      garland(p, t, p.w * 0.76, -6, p.w + 10, p.h * 0.14, p.h * 0.07);
    }
  }
  const banners = p.level >= 3 ? lay.banners : lay.banners.slice(0, 1);
  for (const b of [...banners].reverse()) banner(p, t, b);
  palio(p, t, lay);
  crowd(p, t, lay);
  alfaia(p, t, lay.drum * 0.72, lay.drumFoot, lay.drum, true);
  alfaia(p, t, p.w - lay.drum * 0.72, lay.drumFoot, lay.drum, false);
  calmCenter(p, t.bg, 0.62, 1.15);
  grain(p, 0.06, true);
  vignette(p, t.ink, 0.45);
}
